import {AlmeState} from "../../../wireup/AlmeState";
import {AnyAction, Reducer, Store} from "redux";
import AlmeUIModule from "../../../types/module";
import {InitializationPromises} from "../../../types/initialization";
import {StartingTriggerOperator, StateTriggerOperator, StateTriggers} from "../../../settings/state-triggers";
import watch from 'redux-watch';
import {triggerObjectStateChange, triggerValueStateChange, TriggerValueStateChangeAction} from "./models/Actions";
import objectPath from 'object-path';
import {Dictionary} from "../../../types/rendering";


export default class StateTriggerer extends AlmeUIModule
{

    //#region Public Interface

    initialize(store: Store<AlmeState>): void | InitializationPromises
    {
        super.initialize(store);

        this.setupStateWatches(this._settings.stateTriggers);
    }

    getReducer(): Reducer<AlmeState>
    {
        return (state: AlmeState, action: AnyAction): AlmeState =>
        {
            let newState: AlmeState, existingObject: Dictionary, newObject: Dictionary;
            const triggerAction = action as TriggerValueStateChangeAction<unknown>;

            switch (action.type)
            {
                case "TRIGGER_VALUE_STATE_CHANGE":
                    newState = { ...state };
                    objectPath.set(newState, triggerAction.statePath, triggerAction.value);

                    return newState;

                case "TRIGGER_OBJECT_STATE_CHANGE":
                    newState = { ...state };
                    existingObject = objectPath.get(newState, triggerAction.statePath);
                    newObject = Object.keys(existingObject).reduce((currentObject, key) =>
                    {
                        return { ...currentObject, [key]: triggerAction.value };
                    }, {} as Dictionary);

                    objectPath.set(newState, triggerAction.statePath, newObject);

                    return newState;

                default:
                    return state;
            }
        };
    }

    //#endregion



    //#region Helper Methods

    setupStateWatches<T>(triggers: StateTriggers<T>[]): void
    {
        triggers.forEach(trigger =>
        {
            trigger.startingTriggers.forEach(startingTrigger =>
            {
                const watcher = watch(this._store?.getState.bind(this), startingTrigger.statePath);

                this._store?.subscribe(watcher((newValue: T, oldValue: T) =>
                {
                    if (newValue !== oldValue)
                    {
                        const shouldTrigger = trigger.startingTriggers.reduce((shouldFireTrigger, currentStartingTrigger) =>
                        {
                            const { statePath, operator, value } = currentStartingTrigger;
                            const currentValue = objectPath.get(this._store?.getState(), statePath);
                            const startingTriggerValue = this.evaluateExpression(currentValue, operator, value);

                            return this.evaluateStateTriggerExpression(shouldFireTrigger, trigger.operator, startingTriggerValue);
                        }, this.startingReduceValueForOperator(trigger.operator));

                        if (shouldTrigger)
                        {
                            trigger.setValueStatePaths?.forEach(statePath =>
                                this._store?.dispatch(triggerValueStateChange(trigger.toValue, statePath)));
                            trigger.setObjectStatePaths?.forEach(statePath =>
                                this._store?.dispatch(triggerObjectStateChange(trigger.toValue, statePath)));
                        }
                    }
                }));
            });
        });
    }

    evaluateExpression<T>(leftHandSide: T, operator: StartingTriggerOperator, rightHandSide: T): boolean
    {
        switch (operator)
        {
            case "equal":
                return  leftHandSide === rightHandSide;

            case "notEqual":
                return  leftHandSide !== rightHandSide;
        }
    }

    evaluateStateTriggerExpression(leftHandSide: boolean, operator: StateTriggerOperator, rightHandSide: boolean): boolean
    {
        switch (operator)
        {
            case "and":
                return leftHandSide && rightHandSide;

            case "or":
                return leftHandSide || rightHandSide;
        }
    }

    startingReduceValueForOperator(operator: StateTriggerOperator): boolean
    {
        switch (operator)
        {
            case "and":
                return true;

            case "or":
                return false;
        }
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/state-triggerer/src/StateTriggerer.ts